/*!
 * Copyright © 2020-2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import {
  EApiErrorCodes,
  EApiRequestStatus,
  ICredentialsRequestAction,
  IFailureAction,
  IInitialiseResponse,
  IVerificationCodeRequestAction,
  SET_YOUR_PASSWORD_ROUTE_NAME,
  SUCCESSFULLY_VERIFIED_ROUTE_NAME,
  VERIFY_YOUR_ACCOUNT_ROUTE_NAME
} from '../../models';
import { PendoService, SetPasswordService } from '../../services';
import {
  ResendVerificationCodeFailure,
  ResendVerificationCodeSuccess,
  SendVerificationCodeFailure,
  SendVerificationCodeSuccess,
  SetCredentialsFailure,
  SetCredentialsSuccess,
  UpdateRequestStatus,
  UserActionTypes
} from '../actions';
import { getPassword, getSessionToken, getUsername } from '../selectors';
import { IUserState } from '../state';

@Injectable()
export class UserEffects {
  BANNER_DISPLAY_TIME_MILLISECONDS = 5000;

  initialiseSetPasswordEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.SendVerificationCodeRequest),
      exhaustMap((action: IVerificationCodeRequestAction) =>
        this.setPasswordService.initialiseSetPassword(action.username).pipe(
          map((response: IInitialiseResponse) => SendVerificationCodeSuccess({ ...response })),
          catchError((error: HttpErrorResponse) => {
            if ([EApiErrorCodes.MalformedRequest, EApiErrorCodes.SessionTimeout, EApiErrorCodes.ServerError].includes(error.status)) {
              this.pendoService.trackErrorEvent('Account | Send Verification Code Failure', error.status);
              return of(SendVerificationCodeFailure({ error }));
            }

            return of(
              UpdateRequestStatus({
                setPasswordRequestStatus: EApiRequestStatus.Initial
              })
            );
          })
        )
      )
    )
  );

  sendVerificationCodeSuccessEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.SendVerificationCodeSuccess),
        tap(() => this.router.navigate([`/${VERIFY_YOUR_ACCOUNT_ROUTE_NAME}`]))
      ),
    { dispatch: false }
  );

  reInitialiseSetPasswordEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.ResendVerificationCodeRequest),
      withLatestFrom(this.store.pipe(select(getUsername))),
      exhaustMap(([action, username]: [Action, string]) =>
        this.setPasswordService.initialiseSetPassword(username).pipe(
          map((response: IInitialiseResponse) => ResendVerificationCodeSuccess({ ...response })),
          catchError((error: HttpErrorResponse) => {
            if ([EApiErrorCodes.MalformedRequest, EApiErrorCodes.SessionTimeout, EApiErrorCodes.ServerError].includes(error.status)) {
              this.pendoService.trackErrorEvent('Account | Resend Verification Code Failure', error.status);
              return of(ResendVerificationCodeFailure({ error }));
            }

            return of(
              UpdateRequestStatus({
                resendCodeRequestStatus: EApiRequestStatus.Initial
              })
            );
          })
        )
      )
    )
  );

  resendVerificationCodeSuccessEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.ResendVerificationCodeSuccess),
      switchMap(() => timer(this.BANNER_DISPLAY_TIME_MILLISECONDS)),
      map(() => UpdateRequestStatus({ resendCodeRequestStatus: EApiRequestStatus.Initial }))
    )
  );

  resendVerificationCodeFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.ResendVerificationCodeFailure),
      filter((action: IFailureAction) => action.error.status === EApiErrorCodes.ServerError),
      map(() => {
        this.router.navigate([`/${SET_YOUR_PASSWORD_ROUTE_NAME}`]);
        return UpdateRequestStatus({
          setPasswordRequestStatus: EApiRequestStatus.Initial,
          resendCodeRequestStatus: EApiRequestStatus.Failure,
          verifyAccountRequestStatus: EApiRequestStatus.Initial
        });
      })
    )
  );

  completeSetPasswordEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.SetCredentialsRequest),
      withLatestFrom(this.store.pipe(select(getSessionToken)), this.store.pipe(select(getPassword))),
      exhaustMap(([action, sessionToken, password]: [ICredentialsRequestAction, string, string]) =>
        this.setPasswordService.completeSetPassword(sessionToken, password, action.verificationCode).pipe(
          map(() => SetCredentialsSuccess()),
          catchError((error: HttpErrorResponse) => {
            this.pendoService.trackErrorEvent('Account | Set Credentials Failure', error.status);
            return of(SetCredentialsFailure({ error }));
          })
        )
      )
    )
  );

  setCredentialsSuccessEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.SetCredentialsSuccess),
        tap(() => this.router.navigate([`/${SUCCESSFULLY_VERIFIED_ROUTE_NAME}`]))
      ),
    { dispatch: false }
  );

  setCredentialsFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.SetCredentialsFailure),
      filter((action: IFailureAction) => action.error.status !== EApiErrorCodes.VerificationCodeInvalid),
      map(() => {
        this.router.navigate([`/${SET_YOUR_PASSWORD_ROUTE_NAME}`]);
        return UpdateRequestStatus({
          setPasswordRequestStatus: EApiRequestStatus.Initial,
          resendCodeRequestStatus: EApiRequestStatus.Initial
        });
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<IUserState>,
    private readonly setPasswordService: SetPasswordService,
    private readonly pendoService: PendoService,
    private readonly router: Router
  ) {}
}
